import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  ArrowLeft, ArrowRight, UtensilsCrossed, MapPin, Star,
  Flame, Soup, Cookie, IceCreamCone, Beef, Drumstick, ChefHat, Cherry, Apple, Wheat, Ticket, IceCreamBowl,
} from "lucide-react";
import { motion } from "framer-motion";
import { Seo, PageSchema } from "@/components/Seo";
import { ShareButton } from "@/components/ShareButton";
import { ShareBar } from "@/components/ShareBar";
import { ExploreLinks } from "@/components/ExploreLinks";
import { GygTourBand } from "@/components/GetYourGuide";
import { Reveal } from "@/components/motion";

const SITE = "https://cphhighlights.com";

const DISHES = [
  {
    icon: UtensilsCrossed,
    name: "Smørrebrød",
    tag: "The national lunch",
    img: "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/fuwjhfpl_20260823_083044.webp",
    alt: "Smørrebrød — Danish open rye-bread sandwiches with classic toppings",
    body: "Buttered rye bread piled with toppings — pickled herring, roast beef with remoulade, egg and prawns. The national lunch, with its own rules: herring first, then fish, then meat.",
    link: { label: "The smørrebrød guide", to: "/smorrebrod" },
  },
  {
    icon: Flame,
    name: "Stegt flæsk med persillesovs",
    tag: "The national dish",
    img: "/images/food/stegt-flaesk.webp",
    alt: "Stegt flæsk med persillesovs — crispy fried pork belly with boiled potatoes and parsley sauce",
    body: "Crispy fried pork belly with boiled potatoes and a mild parsley sauce. Salty, simple and perfect — Danes voted it the national dish in 2014. On menus just look for 'stegt flæsk'.",
    spots: [
      { name: "Nyhavns Færgekro", area: "Nyhavn 5, Indre By", hint: "ad libitum Thursdays" },
      { name: "Axelborg Bodega", area: "Axeltorv 1, Indre By", hint: "since 1912" },
      { name: "Restaurant Klubben", area: "Enghavevej 4, Vesterbro", hint: "ad libitum" },
      { name: "Christianshavns Færgecafé", area: "Strandgade 50, Christianshavn", hint: "ad libitum Wednesdays" },
    ],
  },
  {
    icon: Drumstick,
    name: "Flæskesteg",
    tag: "The Christmas king",
    img: "/images/food/flaeskesteg.webp",
    alt: "Flæskesteg — Danish roast pork with crackling, gravy, caramelised potatoes and red cabbage",
    body: "Roast pork with shattering crackling, brown gravy, red cabbage and caramelised potatoes. This is the Danish Christmas dinner — but it shows up year-round, also in the beloved flæskesteg sandwich.",
    spots: [
      { name: "Københavner Caféen", area: "Badstuestræde 10, Indre By" },
      { name: "Restaurant Karla", area: "Dantes Plads 1, København V" },
      { name: "Restaurant Puk", area: "Vandkunsten 8, Indre By" },
      { name: "Det Lille Apotek", area: "Store Kannikestræde 15, Indre By", hint: "oldest in town, since 1720" },
      { name: "Frk. Barners Kælder", area: "Helgolandsgade 8A, Vesterbro" },
    ],
  },
  {
    icon: Soup,
    name: "Hakkebøf med bløde løg",
    tag: "Bodega comfort",
    img: "/images/food/hakkeboef.webp",
    alt: "Hakkebøf med bløde løg — Danish minced-beef patty with soft fried onions, brown gravy and potatoes",
    body: "A minced-beef patty smothered in soft fried onions and brown gravy, with potatoes. The taste of a proper old Copenhagen bodega.",
    spots: [
      { name: "Restaurant Karla", area: "Dantes Plads 1, København V", hint: "250g or a giant 500g" },
      { name: "Restaurant Puk", area: "Vandkunsten 8, Indre By", hint: "giant 350g" },
      { name: "Frk. Barners Kælder", area: "Helgolandsgade 8A, Vesterbro" },
      { name: "Restaurant Klubben", area: "Enghavevej 4, Vesterbro", hint: "350g, two fried eggs" },
      { name: "Nyhavnskroen", area: "Nyhavn 47, Nyhavn" },
    ],
  },
  {
    icon: Beef,
    name: "Frikadeller",
    tag: "Everyday classic",
    img: "/images/food/frikadeller.webp",
    alt: "Frikadeller — Danish pan-fried meatballs with potatoes, gravy, red cabbage and cucumber salad",
    body: "Pan-fried pork-and-veal meatballs, crisp outside and soft in the middle. With potatoes and gravy for dinner — cold on rye bread the day after.",
  },
  {
    icon: ChefHat,
    name: "Tarteletter",
    tag: "Old-school starter",
    img: "/images/food/tarteletter.webp",
    alt: "Tarteletter — Danish puff-pastry shells with creamy chicken and asparagus",
    body: "Little puff-pastry shells filled with creamy chicken and asparagus. Retro, rich and very Danish.",
  },
  {
    icon: Star,
    name: "Pølser — the hotdog",
    tag: "Street food original",
    img: "/planner/hotdog-cph.webp",
    alt: "A proper Danish hotdog from a pølsevogn — with remoulade, mustard and crispy onions",
    body: "From a pølsevogn wagon: first pick your sausage — the red one (rød pølse), boiled and snappy, or the brown grilled one (ristet). Then order it 'med det hele' — with the works: mustard, ketchup, remoulade, raw and crispy onions. The cheapest classic in town and a small piece of Danish culture.",
    link: { label: "More cheap eats", to: "/budget" },
  },
  {
    icon: Cherry,
    name: "Rødgrød med fløde",
    tag: "Summer dessert",
    img: "/images/food/rodgrod.webp",
    alt: "Rødgrød med fløde — Danish red berry porridge with cold cream",
    body: "The ultimate Danish dessert test — mostly because foreigners can't pronounce it. A thick porridge of summer's red berries, swimming in cold whipping cream.",
  },
  {
    icon: IceCreamBowl,
    name: "Koldskål",
    tag: "Summer thing",
    img: "/images/food/koldskaal.webp",
    alt: "Koldskål — cold Danish buttermilk soup with kammerjunker biscuits and strawberries",
    body: "Cold buttermilk 'soup' with vanilla and lemon, topped with crunchy kammerjunker biscuits — and strawberries when it's warm. The taste of a Danish summer evening.",
  },
  {
    icon: Apple,
    name: "Gammeldags æblekage",
    tag: "Not actually a cake",
    img: "/images/food/aeblekage.webp",
    alt: "Gammeldags æblekage — Danish apple purée layered in a glass with toasted crumbs, whipped cream and redcurrant jelly",
    body: "No sponge in sight: apple purée layered in a glass with toasted breadcrumbs or crushed macaroons, topped with whipped cream and redcurrant jelly.",
  },
  {
    icon: Wheat,
    name: "Risengrød",
    tag: "All December long",
    img: "/images/food/risengrod.webp",
    alt: "Risengrød — Danish hot rice porridge with a knob of butter, cinnamon and sugar",
    body: "Hot rice porridge with a knob of butter, cinnamon and sugar.",
    spotsLabel: "GRØD SERVES RISENGRØD EVERY 24TH, COUNTING DOWN TO CHRISTMAS EVE — AND ALL DECEMBER",
    spots: [
      { name: "Grød", hint: "Torvehallerne · Hal 2, Stade 8A", area: "Linnésgade 17, København K" },
      { name: "Grød", hint: "Vesterbro", area: "Vesterbrogade 105B, København V" },
      { name: "Grød", hint: "Christianshavn", area: "Prinsessegade 23, København K" },
      { name: "Grød", hint: "Jægersborggade", area: "Jægersborggade 50, Nørrebro" },
      { name: "Grød", hint: "Elmegade", area: "Birkegade 2, Nørrebro" },
      { name: "Grød", hint: "Frederiksberg", area: "Falkoner Allé 34, Frederiksberg C" },
    ],
    link: { label: "Christmas in Copenhagen", to: "/christmas" },
  },
  {
    icon: Cookie,
    name: "Æbleskiver",
    tag: "Christmas treat",
    img: "/images/food/aebleskiver.webp",
    alt: "Æbleskiver — round Danish pancake puffs with powdered sugar and jam",
    body: "Round pancake puffs served warm with jam and a snowfall of powdered sugar. Not to be confused with the little Dutch puffs (poffertjes) — the Danish ones are bigger and properly round. A Christmas-market essential from November.",
    link: { label: "Christmas in Copenhagen", to: "/christmas" },
  },
  {
    icon: IceCreamCone,
    name: "Risalamande",
    tag: "Christmas dessert",
    img: "/images/food/risalamande.webp",
    alt: "Risalamande — Danish cold rice pudding with almonds and warm cherry sauce",
    body: "Cold rice pudding folded with whipped cream and chopped almonds, drowned in warm cherry sauce. One portion hides a whole almond — find it and you win the little 'mandelgave' prize.",
    link: { label: "The Christmas dinner guide", to: "/christmas-food" },
  },
];

const MAPS = (name, area) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${name}, ${area}, København`)}`;

const FAQS = [
  {
    q: "What is Denmark's national dish?",
    a: "Stegt flæsk med persillesovs — crispy fried pork belly with boiled potatoes and parsley sauce. Danes voted it the national dish in 2014, and it's still on traditional menus all over Copenhagen.",
  },
  {
    q: "What is a traditional Danish dinner?",
    a: "Hearty and pork-forward: frikadeller (meatballs), hakkebøf (minced beef with soft onions) or flæskesteg (roast pork), almost always with potatoes and brown gravy. Lunch is usually smørrebrød — open sandwiches on rye.",
    link: { to: "/smorrebrod", label: "The smørrebrød guide" },
  },
  {
    q: "What is a Danish hotdog?",
    a: "A pølse from a pølsevogn — the little hotdog wagons on squares and stations. First you pick the sausage: a red boiled one (rød pølse) or a brown grilled one (ristet). Then order it 'med det hele' — with everything: mustard, ketchup, remoulade, raw and crispy onions and pickles.",
  },
  {
    q: "What food is Copenhagen famous for?",
    a: "Three things, high to low: smørrebrød, the open rye-bread sandwiches piled with herring, eggs or roast pork; wienerbrød, the buttery pastries the rest of the world simply calls a 'Danish'; and New Nordic cooking, the local fine-dining movement that turned the city into a food destination. Start with a smørrebrød lunch — it's the one classic you can't get right anywhere else.",
    links: [
      { to: "/smorrebrod", label: "The smørrebrød guide" },
      { to: "/bakeries", label: "The bakery guide" },
    ],
  },
  {
    q: "What are some popular Danish pastries?",
    a: "Danes call them wienerbrød — 'Viennese bread' — and any good bakery makes them fresh every morning. The ones to know: kanelsnegl, the cinnamon swirl; spandauer, with custard or jam in the middle; and tebirkes, a poppy-seed roll with a soft marzipan centre.",
    link: { to: "/bakeries", label: "The bakery guide" },
  },
  {
    q: "Is traditional Danish food vegetarian-friendly?",
    a: "Honestly, the classics are meat-heavy. But Copenhagen is one of Europe's easiest cities for vegetarians — most traditional restaurants now offer a plant-based take, and the New Nordic scene does vegetables like nowhere else.",
  },
  {
    q: "Which restaurant is the most expensive in Copenhagen?",
    a: "Alchemist and Noma battle for the top spot — expect around 4,000–5,000 kr. per person for the tasting menu alone, before wine pairings that can nearly double it. Three-Michelin-star Geranium sits just below. The good news: Copenhagen's best everyday food — smørrebrød, bakeries, food markets — costs a fraction of that.",
  },
  {
    q: "Which restaurant is the most famous in Copenhagen?",
    a: "Noma — voted the world's best restaurant five times and the reason Copenhagen became a food destination. Its New Nordic cooking reshaped fine dining worldwide. Getting a table takes luck and planning, but the city's food scene is full of places run by Noma alumni.",
  },
  {
    q: "Does Copenhagen have a food market?",
    a: "Several. Torvehallerne — the two glass food halls by Nørreport — is the central one, open all year for everything from smørrebrød to chocolate. Tivoli Food Hall, right by the gardens' main entrance, is another all-year option — and you don't need a Tivoli ticket to get in. Reffen is the big outdoor street-food market on the Refshaleøen harbourfront in the warmer months, and Broens Gadekøkken does harbour-side street food by the Inner Harbour Bridge in season.",
    link: { to: "/areas/refshaleoen", label: "The Refshaleøen guide" },
  },
  {
    q: "What is New Nordic cuisine?",
    a: "The movement Noma started in the 2000s: local, seasonal Nordic ingredients — foraged, fermented, pickled — served as inventive tasting menus. It turned Copenhagen into one of the world's food capitals and still shapes menus across the city, from Michelin rooms to casual wine bars.",
  },
];

const FAQ_LD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const DISHES_LD = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "Traditional Danish dishes",
  itemListElement: DISHES.map((d, i) => ({
    "@type": "ListItem",
    position: i + 1,
    item: { "@type": "MenuItem", name: d.name, description: d.body },
  })),
};

// Every recommended spot as Restaurant schema (deduped — some serve several classics).
const SPOT_SET = [...new Map(DISHES.flatMap((d) => (d.spots || []).map((s) => [`${s.name}|${s.area}`, s]))).values()];

const RESTAURANTS_LD = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "Where to eat traditional Danish food in Copenhagen",
  itemListElement: SPOT_SET.map((s, i) => ({
    "@type": "ListItem",
    position: i + 1,
    item: {
      "@type": "Restaurant",
      name: s.name,
      servesCuisine: "Danish",
      address: {
        "@type": "PostalAddress",
        streetAddress: s.area.split(",")[0],
        addressLocality: "Copenhagen",
        addressCountry: "DK",
      },
    },
  })),
};

export default function DanishFoodPage() {
  return (
    <main data-testid="danish-food-page" className="bg-[#F9F8F6] text-black font-body">
      <Seo
        title="Traditional Danish Food in Copenhagen — Dishes & Where to Try Them"
        description="Smørrebrød, stegt flæsk (Denmark's national dish), flæskesteg, frikadeller, æbleskiver and more — what the Danish classics are, and where to try them in Copenhagen."
        path="/danish-food"
        image={`${SITE}/images/food/stegt-flaesk.webp`}
      />
      <PageSchema
        name="Traditional Danish Food in Copenhagen — Dishes & Where to Try Them"
        description="Smørrebrød, stegt flæsk (Denmark's national dish), flæskesteg, frikadeller, æbleskiver and more — what the Danish classics are, and where to try them in Copenhagen."
        path="/danish-food"
        crumb="Danish food"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(FAQ_LD)}</script>
        <script type="application/ld+json">{JSON.stringify(DISHES_LD)}</script>
        <script type="application/ld+json">{JSON.stringify(RESTAURANTS_LD)}</script>
      </Helmet>

      {/* hero — Danebrog red, like the flag behind the national dish */}
      <section className="relative overflow-hidden bg-[#13141A] text-white border-b border-white/20">
        <div className="absolute inset-0">
          <img src={"https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/fuwjhfpl_20260823_083044.webp"} alt="" aria-hidden="true" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#13141A] via-[#13141A]/70 to-[#13141A]/40" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 pt-24 md:pt-28 pb-12 md:pb-24">
          <div className="flex items-center gap-3 mb-10 md:mb-12">
            <Link
              to="/"
              data-testid="danish-food-back"
              className="inline-flex items-center gap-2 font-body font-700 text-sm bg-white text-black px-5 py-2.5 rounded-full border border-zinc-200 hover:bg-[#F9F8F6] transition-colors"
            >
              <ArrowLeft size={16} /> Back to start
            </Link>
            <ShareButton testid="danish-food-share" />
          </div>
          <Reveal>
            <p className="font-body font-800 text-xs uppercase tracking-[0.3em] eyebrow text-white mb-4">
              TRADITIONAL DANISH FOOD
            </p>
            <h1 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl sm:text-5xl lg:text-6xl max-w-3xl">
              The national dish<br />
              <span className="font-editorial italic font-600">and the other classics</span>
            </h1>
            <p className="font-body text-base md:text-lg text-white/85 max-w-2xl mt-6">
              Forget New Nordic for a night — this is the food Danes actually grew up on. Hearty,
              pork-forward, with potatoes and gravy, and best eaten in an old bodega with a beer.
              Photos by us, plates by Copenhagen.
            </p>
          </Reveal>
        </div>
      </section>

      {/* the dishes */}
      <section className="bg-[#F9F8F6]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {DISHES.map((d, i) => (
              <Reveal key={d.name} y={30} delay={Math.min(i * 0.04, 0.2)}>
                <article
                  data-testid={`danish-food-dish-${i}`}
                  className="h-full bg-white border border-zinc-200 shadow-brut rounded-2xl overflow-hidden flex flex-col"
                >
                  {d.img && (
                    <img
                      src={d.img}
                      alt={d.alt}
                      loading="lazy"
                      className="w-full h-56 md:h-64 object-cover"
                    />
                  )}
                  <div className="p-6 md:p-8 flex flex-col flex-1">
                    <span className="font-body font-800 text-xs tracking-[0.25em] uppercase text-[#DF3226]">{d.tag}</span>
                    <h2 className="font-display font-900 tracking-tight text-2xl md:text-3xl mt-2">{d.name}</h2>
                    <p className="font-body text-sm md:text-base text-black/70 mt-3 leading-relaxed flex-1">{d.body}</p>
                    {d.spots && (
                      <div className="mt-3 pt-3 border-t border-dotted border-zinc-300">
                        <p className="font-body font-800 text-[11px] tracking-[0.2em] text-black/50 mb-1">{d.spotsLabel || "WHERE TO TRY IT"}</p>
                        <ul className="divide-y divide-zinc-100">
                          {d.spots.map((s) => (
                            <li key={s.name} className="py-1 flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-0 sm:gap-3">
                              <p className="font-body text-sm">
                                <strong className="font-display font-800">{s.name}</strong>
                                {s.hint && <span className="text-black/50 text-xs"> · {s.hint}</span>}
                              </p>
                              <a
                                href={MAPS(s.name, s.area)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="shrink-0 inline-flex items-center gap-1 font-body font-700 text-[11px] text-black/60 hover:text-[#DF3226] transition-colors"
                              >
                                <MapPin size={11} className="text-[#DF3226]" /> {s.area}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {d.link && (
                      <Link
                        to={d.link.to}
                        className="mt-4 inline-flex items-center gap-2 font-body font-800 text-sm text-[#DF3226] underline underline-offset-4 hover:text-black transition-colors"
                      >
                        {d.link.label}
                      </Link>
                    )}
                  </div>
                </article>
              </Reveal>
            ))}
          </div>

          <Reveal y={20}>
            <Link
              to="/advertise"
              data-testid="danish-food-missing"
              className="mt-10 flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white border-2 border-dashed border-zinc-300 hover:border-[#DF3226] hover:-translate-y-0.5 transition-all p-6 md:p-8"
            >
              <div>
                <span className="font-body font-800 text-[11px] tracking-widest text-[#DF3226]">
                  THIS SPOT IS OPEN
                </span>
                <h3 className="font-display font-900 tracking-tight text-2xl leading-none mt-2">
                  Is your restaurant missing?
                </h3>
                <p className="font-body text-sm md:text-base text-black/70 mt-2">
                  Serving the classics? Get in front of the travellers deciding what's for dinner
                  in Copenhagen.
                </p>
              </div>
              <span className="shrink-0 inline-flex items-center gap-1.5 font-body font-800 text-sm text-[#DF3226]">
                Advertise with us
              </span>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-black text-white border-t border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24 text-center md:text-left flex flex-col md:flex-row items-center justify-between gap-8">
          <h2 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl md:text-5xl">
            Hungry for the classics?<br />
            <span className="font-editorial italic font-600">Build them into your days</span>
          </h2>
          <Link
            to="/planner"
            data-testid="danish-food-to-planner"
            className="inline-flex items-center gap-2 font-display font-900 text-lg bg-[#DF3226] text-white px-8 py-4 rounded-full border border-white/20 shadow-brut hover:-translate-y-0.5 hover:shadow-xl transition-all"
          >
            Open the planner
          </Link>
        </div>
      </section>

      {/* Book a tour — taste the classics with a local */}
      <GygTourBand tourId="21977" label="BOOK A TOUR" />

      <ExploreLinks
        testid="explore-food"
        links={[
          { label: "Open sandwiches, ranked — the smørrebrød guide", to: "/smorrebrod" },
          { label: "Something sweet — the bakery guide", to: "/bakeries" },
          { label: "Eat well for less — Copenhagen on a budget", to: "/budget" },
        ]}
      />

      {/* FAQ — last, before the share bar */}
      <section className="bg-white border-t border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-16">
          <h2 className="font-display font-900 tracking-tighter text-3xl md:text-4xl mb-8">
            Danish food, <span className="font-editorial italic font-600">answered</span>
          </h2>
          <div className="max-w-3xl divide-y divide-zinc-200 border-y border-dotted border-zinc-300">
            {FAQS.map((f, i) => (
              <details key={f.q} data-testid={`danish-food-faq-${i}`} className="group py-5">
                <summary className="flex items-center justify-between gap-4 cursor-pointer font-display font-900 text-lg md:text-xl tracking-tight list-none">
                  {f.q}
                  <span className="font-body text-[#DF3226] text-2xl leading-none group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="font-body text-sm md:text-base text-black/70 leading-relaxed mt-3 max-w-2xl">
                  {f.a}
                  {(f.links || (f.link ? [f.link] : [])).map((l, j) => (
                    <Link
                      key={l.to + l.label}
                      to={l.to}
                      data-testid={`danish-food-faq-link-${i}-${j}`}
                      className="group flex items-center gap-2 mt-3 font-body font-700 text-base text-black/60 hover:text-[#DF3226] transition-colors w-fit"
                    >
                      <ArrowRight size={16} className="text-[#DF3226] shrink-0 group-hover:translate-x-1 transition-transform" />
                      {l.label}
                    </Link>
                  ))}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>
      <ShareBar />
    </main>
  );
}
